import React, { useState } from 'react';
import { X, Copy, Globe, Mail, Terminal, Check, Info, ShieldCheck } from 'lucide-react';
import type {
    DomainVerificationItem,
    DomainVerificationMethod,
    DomainVerificationInstructions
} from '@/lib/api/types';

// Method Badge Component
const MethodBadge: React.FC<{ method: DomainVerificationMethod }> = ({ method }) => {
    const config = method === 'dns'
        ? { icon: <Globe className="h-3 w-3" />, label: 'DNS_RECORD' }
        : { icon: <Mail className="h-3 w-3" />, label: 'ADMIN_MAIL' };

    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-[#00FFFF]/30 bg-[#00FFFF]/5 text-[#00FFFF] text-[9px] font-bold uppercase tracking-widest">
            {config.icon}
            {config.label}
        </span>
    );
};

interface DomainVerificationInstructionsModalProps {
    domain: DomainVerificationItem;
    instructions: DomainVerificationInstructions | null;
    onClose: () => void;
}

const DomainVerificationInstructionsModal: React.FC<DomainVerificationInstructionsModalProps> = ({
    domain,
    instructions,
    onClose
}) => {
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    const copyToClipboard = async (text: string, key: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    if (!instructions) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4 font-mono">
            <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-2xl max-w-2xl w-full shadow-[0_0_60px_rgba(0,0,0,0.9)] overflow-hidden animate-in fade-in zoom-in-95 duration-300">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-[#1A1D24] bg-[#12151C]">
                    <div className="flex items-center gap-3">
                        <Terminal className="h-5 w-5 text-[#00FFFF]" />
                        <h3 className="text-[13px] font-bold text-white uppercase tracking-[0.2em]">
                            Verification_Instructions
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-500 hover:text-[#FF3333] hover:bg-[#1A1D24] rounded-lg transition-all border border-transparent hover:border-[#1A1D24]"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-8 space-y-6">
                    {/* Domain Overview */}
                    <div className="p-4 bg-[#050505] border border-[#1A1D24] rounded-xl flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-2.5 bg-[#00FFFF]/10 rounded-lg border border-[#00FFFF]/20">
                                <Globe className="h-5 w-5 text-[#00FFFF]" />
                            </div>
                            <div>
                                <p className="text-[9px] text-slate-500 uppercase tracking-widest mb-0.5">Target_Node</p>
                                <span className="text-[14px] font-bold text-white uppercase tracking-tight">
                                    {domain.domain}
                                </span>
                            </div>
                        </div>
                        <MethodBadge method={domain.method} />
                    </div>

                    {/* DNS Instructions */}
                    {domain.method === 'dns' && instructions.txt_record && (
                        <div className="space-y-4 animate-in slide-in-from-bottom-2">
                            <h4 className="text-[11px] font-bold text-[#00FFFF] uppercase tracking-widest flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4" /> Required_DNS_Parameters
                            </h4>

                            <div className="space-y-3">
                                {[
                                    { label: 'Host/Name', val: instructions.txt_record.name, key: 'name' },
                                    { label: 'Record_Value', val: instructions.txt_record.value, key: 'value' }
                                ].map((field) => (
                                    <div key={field.key} className="space-y-1.5">
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest ml-1">
                                            {field.label}:
                                        </label>
                                        <div className="flex items-center gap-2 p-3 bg-black border border-[#1A1D24] rounded-lg group hover:border-slate-600 transition-colors">
                                            <code className="text-[12px] text-[#02DFC2] flex-1 break-all font-mono leading-relaxed">
                                                {field.val}
                                            </code>
                                            <button
                                                onClick={() => copyToClipboard(field.val, field.key)}
                                                className={`p-2 transition-all rounded-md ${copiedKey === field.key
                                                        ? 'bg-[#50A970] text-black shadow-[0_0_10px_#50A970]'
                                                        : 'text-slate-500 hover:text-[#00FFFF] hover:bg-[#12151C]'
                                                    }`}
                                            >
                                                {copiedKey === field.key ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="flex gap-3 p-4 bg-[#EAB308]/5 border border-[#EAB308]/20 rounded-xl mt-6">
                                <Info className="h-4 w-4 text-[#EAB308] flex-shrink-0 mt-0.5" />
                                <p className="text-[11px] text-[#EAB308]/80 leading-relaxed uppercase tracking-wide">
                                    Inject this TXT record into your global DNS zone. TTL propagation can take up to 24 hours. Verification will attempt auto-sync.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Email Instructions */}
                    {domain.method === 'email' && (
                        <div className="space-y-4 animate-in slide-in-from-bottom-2">
                            <h4 className="text-[11px] font-bold text-[#A855F7] uppercase tracking-widest flex items-center gap-2">
                                <Mail className="w-4 h-4" /> Authorization_Chain
                            </h4>

                            {instructions.email && (
                                <div className="p-4 bg-[#50A970]/5 border border-[#50A970]/20 rounded-xl flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <ShieldCheck className="h-4 w-4 text-[#50A970]" />
                                        <span className="text-[12px] text-white font-bold tracking-tight italic lowercase">
                                            {instructions.email}
                                        </span>
                                    </div>
                                    <span className="text-[9px] font-bold text-[#50A970] uppercase tracking-widest">Awaiting_Ack</span>
                                </div>
                            )}

                            {instructions.instructions && (
                                <div className="p-4 bg-[#050505] border border-[#1A1D24] rounded-xl">
                                    <pre className="text-[11px] text-slate-400 whitespace-pre-wrap leading-relaxed uppercase tracking-wider">
                                        {instructions.instructions}
                                    </pre>
                                </div>
                            )}

                            <div className="flex gap-3 p-4 bg-[#00FFFF]/5 border border-[#00FFFF]/20 rounded-xl">
                                <Terminal className="h-4 w-4 text-[#00FFFF] flex-shrink-0 mt-0.5" />
                                <p className="text-[11px] text-[#00FFFF]/80 leading-relaxed uppercase tracking-wide">
                                    A verification hash has been dispatched to the admin node. Execute the embedded link within 24 hours to establish trust.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Status Footer */}
                <div className="bg-[#050505] px-8 py-3 border-t border-[#1A1D24] flex items-center justify-between">
                    <div className="text-[9px] font-bold text-slate-600 uppercase tracking-widest">
                        Handshake_Timeout: 24:00:00
                    </div>
                    {copiedKey && (
                        <div className="text-[9px] font-bold text-[#50A970] uppercase tracking-widest animate-pulse">
                            [COPIED_TO_BUFFER]
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DomainVerificationInstructionsModal;